export const articles = [
  {
    slug: 'gcc-salary-benchmarks-india-2026',
    title: 'GCC Salary Benchmarks in India: 2026 Guide',
    category: 'Talent Intelligence',
    date: 'February 2026',
    author: 'Strive Partners Research',
    readingTime: '8 min read',
    excerpt:
      'Comprehensive salary benchmarks across engineering, data, and product roles at India GCCs — from individual contributors to CXO. What the market actually pays in 2026, by function, level, and location.',
    content: [
      {
        type: 'intro',
        text: "India's GCC landscape has matured dramatically over the past three years. What was once a cost-arbitrage play has evolved into a genuine innovation hub — and compensation structures have followed. The 2025–26 cycle has seen base compensation at Director+ levels exceed ₹1.2 Cr in Bangalore for roles with genuine leadership scope, with total packages reaching ₹2–3 Cr when equity and bonuses are included.",
      },
      {
        type: 'heading',
        text: 'Engineering Bands (Bangalore baseline)',
      },
      {
        type: 'table',
        rows: [
          ['Software Engineer L3–L4', '₹25–45 LPA'],
          ['Senior Engineer L5', '₹50–80 LPA'],
          ['Staff / Principal Engineer', '₹90 LPA – ₹1.5 Cr'],
          ['Engineering Manager', '₹80 LPA – ₹1.2 Cr'],
          ['Director of Engineering', '₹1.2 – ₹2 Cr'],
          ['VP Engineering / Head of Engineering', '₹1.8 – ₹3.5 Cr'],
        ],
      },
      {
        type: 'heading',
        text: 'Data & AI Leadership',
      },
      {
        type: 'text',
        text: 'The premium for AI/ML leadership has widened significantly. Heads of AI commanding ₹2–4 Cr are no longer exceptional, particularly in GCCs with genuine AI product mandates rather than feature-support functions.',
      },
      {
        type: 'table',
        rows: [
          ['Data Analyst / BI Analyst', '₹12–22 LPA'],
          ['Data Scientist (Senior)', '₹35–60 LPA'],
          ['ML Engineer (Senior)', '₹50–90 LPA'],
          ['Head of Data Science', '₹90 LPA – ₹1.6 Cr'],
          ['Chief Data Officer / VP of AI', '₹1.8 – ₹4 Cr'],
        ],
      },
      {
        type: 'heading',
        text: 'Product Leadership',
      },
      {
        type: 'text',
        text: 'Product roles have seen the steepest appreciation, driven by GCCs moving from execution-only mandates to product-first operating models. Directors with global P&L exposure are commanding significant premiums over purely India-facing product leads.',
      },
      {
        type: 'table',
        rows: [
          ['Product Manager (Senior)', '₹35–65 LPA'],
          ['Principal PM / Group PM', '₹70 LPA – ₹1.2 Cr'],
          ['Director of Product', '₹1.2 – ₹2 Cr'],
          ['VP Product', '₹1.8 – ₹3 Cr'],
        ],
      },
      {
        type: 'heading',
        text: 'Location Premiums',
      },
      {
        type: 'table',
        rows: [
          ['Bangalore', '100% (index)'],
          ['Hyderabad', '88–92%'],
          ['Pune', '82–86%'],
          ['Chennai', '78–82%'],
          ['NCR / Delhi', '80–85%'],
        ],
      },
      {
        type: 'heading',
        text: 'Key Trends for 2026',
      },
      {
        type: 'list',
        items: [
          'Senior compression is real but selective — ultra-senior IC roles (Staff+) are converging with M3–M4 management bands in base, though total comp diverges significantly with equity.',
          'AI/ML remains the most over-demanded skill set; expect a 15–25% premium on market rate for genuine ML expertise with production deployment experience.',
          'Stock options are re-emerging as a meaningful component of senior GCC packages, particularly at growth-stage GCCs with a path to strategic value.',
          'Australian-headquartered GCCs are paying a notable premium versus US counterparts at Director+ level, driven by exchange rate dynamics and competitive positioning.',
        ],
      },
    ],
  },
  {
    slug: 'building-gcc-technology-leadership-team-india',
    title: 'Building a GCC Technology Leadership Team in India',
    category: 'Search Practice',
    date: 'January 2026',
    author: 'Strive Partners',
    readingTime: '10 min read',
    excerpt:
      'The founding hire shapes everything. A framework for identifying, assessing, and placing the technology leaders who will define your India centre — from the first VP to the full leadership cohort.',
    content: [
      {
        type: 'intro',
        text: 'The founding hire shapes everything. Who you place as your first India technology leader — whether that is a Site Head, VP Engineering, or Country CTO — determines the culture, the architecture, and ultimately the capability of everything that follows. Getting this wrong costs two years.',
      },
      {
        type: 'heading',
        text: 'The Founding Leader Dilemma',
      },
      {
        type: 'text',
        text: 'There are three distinct archetypes that GCC leaders typically consider when hiring their first India technology head:',
      },
      {
        type: 'list',
        items: [
          'The Corporate Operator — someone who has scaled teams inside a large GCC or MNC. Strength: knows process, compliance, and how to navigate global stakeholders. Risk: can be risk-averse, may under-index on building a genuine engineering culture.',
          'The Startup Veteran — someone who has built from scratch in a high-growth environment. Strength: pace, hiring instinct, and first-principles thinking. Risk: may struggle with governance and reporting requirements of a large parent.',
          'The Specialist Leader — a domain expert (Head of AI, Head of Data). Useful when the GCC has a narrow technical mandate. Risk: limited general leadership experience creates a ceiling on the role.',
        ],
      },
      {
        type: 'text',
        text: 'Most GCCs benefit from archetype 1 or 2 depending on their maturity stage. Pure specialists rarely scale effectively as overall site leaders.',
      },
      {
        type: 'heading',
        text: 'The 4-Factor Assessment Applied',
      },
      {
        type: 'text',
        text: 'At Strive Partners, we assess founding leaders across four dimensions that go beyond technical credentials:',
      },
      {
        type: 'list',
        items: [
          'Adversity Quotient (AQ): Have they built through genuine constraint? Can they operate with ambiguity in stakeholder alignment?',
          'Intelligence Quotient (IQ): Is their technical depth credible enough to earn the respect of a senior engineering team?',
          'Social Quotient (SQ): Do they have the India-specific network to accelerate hiring in the first 90 days?',
          'Emotional Quotient (EQ): Can they manage the cultural complexity of cross-timezone leadership?',
        ],
      },
      {
        type: 'heading',
        text: 'Organisational Structure Models',
      },
      {
        type: 'text',
        text: 'The centre-of-excellence model (federated teams reporting to global function heads) and the integrated site model (all India roles reporting to a Site Head) are the two dominant structures. The right choice depends on the mandate: if the GCC is running standalone product work, the integrated model tends to outperform. If it is primarily supporting global functions, the federated model preserves quality and accountability.',
      },
      {
        type: 'heading',
        text: 'Time-to-Hire Benchmarks',
      },
      {
        type: 'table',
        rows: [
          ['Site Head / VP Engineering', '8–14 weeks'],
          ['Senior Director / Director', '6–10 weeks'],
          ['Engineering Manager', '4–7 weeks'],
          ['Senior Individual Contributor', '3–6 weeks'],
        ],
      },
    ],
  },
  {
    slug: 'india-technology-leadership-hiring-trends-gcc-2026',
    title: 'India Technology Leadership Hiring Trends for GCCs: 2026',
    category: 'Market Intelligence',
    date: 'January 2026',
    author: 'Strive Partners Research',
    readingTime: '12 min read',
    excerpt:
      'Three structural forces are reshaping the India technology talent market for GCCs in 2026. AI/ML leadership scarcity, senior IC compression, and a new leadership contract that transactional GCCs are failing to meet.',
    content: [
      {
        type: 'intro',
        text: 'The India technology talent market in 2026 is characterised by three concurrent forces: rising competition for senior engineers from large-format GCCs, a structural shortage of AI/ML leadership, and a generational shift in what senior talent expects from a role. GCCs that have not updated their employment proposition are finding it significantly harder to close offers at Director and above.',
      },
      {
        type: 'heading',
        text: 'Force 1: AI/ML Leadership Scarcity',
      },
      {
        type: 'text',
        text: 'The number of candidates qualified to lead AI/ML functions at a Director+ level — with both technical credibility and organisational leadership experience — is meaningfully below demand. India has produced world-class ML practitioners, but the cohort with the cross-functional leadership, stakeholder management, and strategic articulation required to head AI at a GCC remains thin. Expect this gap to persist for 18–24 months.',
      },
      {
        type: 'heading',
        text: 'Force 2: Compression at the Top of the IC Track',
      },
      {
        type: 'text',
        text: 'Staff Engineers, Distinguished Engineers, and Principal Product Managers are in greater demand than at any point in the last five years. As GCCs mature from delivery execution to product ownership, the need for senior individual contributors who can anchor technical strategy without formal people management has increased sharply. The supply remains constrained — many senior ICs who would have been available two years ago have moved into management or founded their own ventures.',
      },
      {
        type: 'heading',
        text: 'Force 3: The New Leadership Contract',
      },
      {
        type: 'text',
        text: 'What senior leaders expect from a GCC role has changed fundamentally. Leaders in the Director+ bracket are evaluating roles on four criteria:',
      },
      {
        type: 'list',
        items: [
          'Mandate clarity: Is this a real product build or execution-support work?',
          'Career trajectory: Does this role have a credible path to global functional leadership?',
          'Cultural differentiation: Is the engineering culture at the centre genuinely distinct from the parent?',
          'Equity participation: Is there a mechanism for value creation beyond base compensation?',
        ],
      },
      {
        type: 'text',
        text: 'GCCs that cannot answer these questions credibly in the interview process are losing candidates to product startups and other GCCs that have invested in their employer brand.',
      },
      {
        type: 'heading',
        text: 'High-Demand Roles in 2026',
      },
      {
        type: 'list',
        items: [
          'Head of AI / VP of Machine Learning',
          'Principal Engineer (Platform, Infrastructure, ML)',
          'Director of Product (Global mandate, P&L exposure)',
          'Head of Data Engineering',
          'VP of Engineering (first India leader for scaling GCCs)',
          'Head of DevOps / Platform Engineering',
        ],
      },
      {
        type: 'heading',
        text: 'Structural Changes to Watch',
      },
      {
        type: 'list',
        items: [
          'Several large Australian and UK-headquartered GCCs are establishing Hyderabad centres as overflow from Bangalore saturation, creating a secondary market for senior talent.',
          'The post-IPO liquidity event at several large Indian product companies has released senior talent back into the market at Director+ levels.',
          'Demand for GCC-experienced leaders — specifically those who have navigated the transition from 20 to 200+ headcount — has reached a notable premium over comparable talent without GCC experience.',
        ],
      },
    ],
  },
]
